import { useState } from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { MessageSquare } from 'lucide-react';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/auth';

// Text opt-in for renter flight reminders (Casey, 10/31/26: text a pilot who landed
// back at LGB and hasn't logged the flight 15 minutes later). The checkbox is the
// carrier opt-in, so the wording is versioned and stored with the answer: if the
// copy ever changes, bump the version and old answers still say what was agreed to.
export const TEXT_CONSENT_VERSION = 'renter-texts-v1-2026-10-31';
export const TEXT_CONSENT_COPY =
  'Text me flight reminders from AJM Aviation Ventures, like a reminder to log my flight after I land. ' +
  'Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. Not required to rent.';

export function TextConsentCheckbox({ source }: { source: 'signup' | 'profile' | 'dashboard' }) {
  const { profile, fbUser, demoMode } = useAuth();
  const saved = profile?.smsConsent?.optedIn === true;
  const [checked, setChecked] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function toggle(next: boolean) {
    setChecked(next);
    setErr('');
    if (demoMode || !db || !fbUser) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, 'users', fbUser.uid), {
        smsConsent: { optedIn: next, at: serverTimestamp(), source, version: TEXT_CONSENT_VERSION, text: TEXT_CONSENT_COPY },
      });
    } catch (e) {
      setChecked(!next);
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label className="flex cursor-pointer items-start gap-3 text-sm text-ink">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-navy-700"
          checked={checked}
          disabled={busy}
          onChange={(e) => toggle(e.target.checked)}
        />
        <span>
          {TEXT_CONSENT_COPY}{' '}
          <a href="/privacy#texts" target="_blank" rel="noreferrer" className="underline">Privacy</a> ·{' '}
          <a href="/terms#texts" target="_blank" rel="noreferrer" className="underline">Terms</a>
        </span>
      </label>
      {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
    </div>
  );
}

// One-time ask on the dashboard for pilots who signed up before the checkbox existed.
// Either answer (yes or "No thanks") records a choice, so it never asks twice.
export function TextRemindersPrompt() {
  const { profile, fbUser, demoMode } = useAuth();
  const [hidden, setHidden] = useState(false);
  if (hidden || !profile || profile.smsConsent) return null;

  async function answer(optedIn: boolean) {
    setHidden(true);
    if (demoMode || !db || !fbUser) return;
    await updateDoc(doc(db, 'users', fbUser.uid), {
      smsConsent: { optedIn, at: serverTimestamp(), source: 'dashboard', version: TEXT_CONSENT_VERSION, text: TEXT_CONSENT_COPY },
    }).catch(() => setHidden(false));
  }

  return (
    <div className="card p-5">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-navy-700">
          <MessageSquare size={20} />
        </span>
        <div className="min-w-0">
          <div className="font-display font-semibold text-navy-900">Want a text if you forget to log a flight?</div>
          <p className="mt-1 text-sm text-ink-muted">
            {TEXT_CONSENT_COPY}{' '}
            <a href="/privacy#texts" target="_blank" rel="noreferrer" className="underline">Privacy</a> ·{' '}
            <a href="/terms#texts" target="_blank" rel="noreferrer" className="underline">Terms</a>
          </p>
          <div className="mt-3 flex gap-2">
            <button type="button" className="btn-primary text-sm" onClick={() => answer(true)}>Yes, text me</button>
            <button type="button" className="btn-secondary text-sm" onClick={() => answer(false)}>No thanks</button>
          </div>
        </div>
      </div>
    </div>
  );
}
